import { IconFont, Input, pointer, Text } from '@byecode/ui'
import { Button } from '@byecode/ui/components/Button'
import type { DataSourceAbstract, EndArg, Field } from '@lighthouse/core'
import type { FlowNode, Workflow } from '@lighthouse/shared'
import { getInputInnerTypeMatch, getUpstreamNodesOptions, innerTypeToFieldTypeIconMap, NodeFieldPreview } from '@lighthouse/shared'
import type { JSONContent } from '@tiptap/core'
import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'

import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'
import { useGetUserRecordOptions } from '@/hooks/usePage'

const SCxArgItemWrapper = styled.div`
    margin-bottom: 12px;
`

const SCxHeader = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    height: 32px;
    gap: 6px;
`

const SCxNodeFieldPreviewerWrapper = styled.div`
    position: relative;
    flex: 1;
    margin-top: 2px;
    cursor: pointer;
`

const SCxLabel = styled.div`
    position: relative;
    flex: 1;
    display: flex;
    flex-flow: row nowrap;
    justify-content: flex-start;
    align-items: center;
    height: 100%;
    gap: 6px;
    overflow: hidden;
`

const SCxIcon = styled(IconFont)``

const SCxInput = styled(Input)``

const SCIcon = styled(IconFont)`
    ${pointer};
`

interface ArgItemProps {
    data: EndArg
    field: Field
    dataSourceList: DataSourceAbstract[]
    workflowList: Workflow[]
    allParentNodes?: FlowNode[]
    upstreamNodes?: FlowNode[]
    placeholder?: string
    autoHeight?: boolean
    onItemDelete?: () => void
    onChange?: (value: EndArg) => void
}

export const ArgItem: React.FC<ArgItemProps> = ({
    data,
    field,
    dataSourceList,
    workflowList,
    allParentNodes,
    upstreamNodes,
    placeholder,
    autoHeight = false,
    onItemDelete,
    onChange
}) => {
    const disabledWithVersion = useIsDisabledWithVersion()
    const [isEdit, setIsEdit] = useState(false)
    const userOption = useGetUserRecordOptions()

    const upstreamOptions = useMemo(() => {
        if (allParentNodes && upstreamNodes) {
            return getUpstreamNodesOptions({
                workflowList,
                upstreamNodes,
                allUpstreamNodes: allParentNodes,
                dataSourceList
            })
        }
    }, [allParentNodes, dataSourceList, workflowList, upstreamNodes])

    const handleEditorChange = useCallback(
        (value: JSONContent) => {
            onChange?.({
                ...data,
                value
            })
        },
        [data, onChange]
    )

    if (!data) {
        return null
    }

    return (
        <SCxArgItemWrapper>
            <SCxHeader>
                <SCxLabel>
                    <SCxIcon type={innerTypeToFieldTypeIconMap[data.innerType]} color="var(--color-gray-400)" />
                    {isEdit ? (
                        <SCxInput
                            autoSelect
                            size="sm"
                            placeholder="变量名"
                            defaultValue={data.name}
                            onBlur={e => {
                                const { value } = e.target
                                onChange?.({
                                    ...data,
                                    name: value
                                })
                                setIsEdit(false)
                            }}
                        />
                    ) : (
                        <>
                            <Text size={14}>{data.name}</Text>
                            {!isEdit && (
                                <SCIcon
                                    type="edit"
                                    size={12}
                                    color="var(--color-gray-400)"
                                    onClick={() => {
                                        setIsEdit(true)
                                    }}
                                />
                            )}
                        </>
                    )}
                </SCxLabel>
                <Button type="text" onClick={onItemDelete} icon={<SCxIcon type="Trash" size={16} color="var(--color-gray-400)" />} />
            </SCxHeader>
            <SCxNodeFieldPreviewerWrapper style={{ height: autoHeight ? 'auto' : 36 }}>
                <NodeFieldPreview
                    innerTypes={getInputInnerTypeMatch(field.innerType)}
                    disabled={disabledWithVersion}
                    autoHeight={autoHeight}
                    onBlur={handleEditorChange}
                    value={data?.value}
                    placeholder={placeholder}
                    field={field}
                    upstreamOptions={upstreamOptions}
                    userOption={userOption}
                    dataSourceList={dataSourceList}
                />
            </SCxNodeFieldPreviewerWrapper>
        </SCxArgItemWrapper>
    )
}
